<script lang="ts">
  let props = $props()
  let { answer } = props
</script>

<div
  class="lg:ml-8 mt-2 border-l-2 border-dashed border-blue-800 px-4 lg:px-6 py-3 lg:py-6 bg-sky-50 rounded-r"
>
  {#if answer == 1}
    <p class="text-base lg:text-xl/8 text-gray-700">
      A: <span class="font-bold"
        >“Let’s make sure they have the tools to act without me.”</span
      >
    </p>
    <p class="mt-4 text-sm lg:text-base/8 text-gray-700">
      This response reflects <span class="font-bold"
        >proactive leadership and strong decision-making systems.</span
      >
    </p>
    <p class="mt-2 text-sm lg:text-base/8 text-gray-700">
      You’re not just reacting - you’re equipping. You recognize that your role
      is to build a team that can act with clarity, even in your absence.
    </p>
    <p
      class="mt-2 text-sm lg:text-base/8 text-gray-700 px-0 lg:px-10 italic font-semibold"
    >
      Fast-moving teams don’t wait on availability - they act with confidence
      and speed.
    </p>
    <p class="mt-4 text-sm lg:text-base/8 text-gray-700 italic">It suggests:</p>
    <ul class="list-disc ml-6 lg:ml-8">
      <li class="mt-1 text-sm lg:text-base/7 text-gray-600">
        You’re focused on building autonomy, not dependence.
      </li>
      <li class="mt-1 text-sm lg:text-base/7 text-gray-600">
        You recognize that real leadership means equipping others to move
        confidently without delay.
      </li>
      <li class="mt-1 text-sm lg:text-base/7 text-gray-600">
        You’re working on the system - not just solving the moment.
      </li>
    </ul>

    <p class="mt-6 text-base lg:text-lg/8 text-gray-700 font-bold">
      Level-Up Opportunity
    </p>
    <p class="mt-2 text-sm lg:text-base/8 text-gray-700 ml-0 lg:ml-4">
      Keep going. Now ask yourself: <span class="italic"
        >Can they make the next three decisions without you?</span
      >
    </p>
    <p
      class="mt-1 text-sm lg:text-base/8 text-gray-700 ml-0 lg:ml-4 font-semibold"
    >
      What system, checklist, or framework would make that autonomy sustainable?
    </p>
  {:else if answer == 2}
    <p class="text-base lg:text-xl/8 text-gray-700">
      B: <span class="font-bold">“I’ll get to it later tonight.” </span>
    </p>
    <p class="mt-4 text-sm lg:text-base/8 text-gray-700">
      This shows you’re still functioning as the <span class="font-bold"
        >default decision-maker</span
      > - stepping in to maintain momentum, but at a personal cost.
    </p>
    <p class="mt-2 text-sm lg:text-base/8 text-gray-700">
      You're prioritizing speed in the short term, but not investing in the
      systems that would create lasting independence.
    </p>

    <p
      class="mt-2 text-sm lg:text-base/8 text-gray-700 px-0 lg:px-10 italic font-semibold"
    >
      When decisions wait on your availability, your team’s execution slows to
      your calendar.
    </p>

    <p class="mt-6 text-base lg:text-lg/8 text-gray-700 font-bold">
      Level-Up Opportunity
    </p>
    <p class="mt-2 text-sm lg:text-base/8 text-gray-700 ml-0 lg:ml-4">
      Instead of jumping in, pause and ask: <span class="italic"
        >What’s missing that’s making them wait?</span
      >
    </p>
    <p
      class="mt-1 text-sm lg:text-base/8 text-gray-700 ml-0 lg:ml-4 font-semibold"
    >
      Could you clarify roles, decision rights, or success criteria so they can
      move without you?
    </p>
  {:else if answer == 3}
    <p class="text-base lg:text-xl/8 text-gray-700">
      C: <span class="font-bold">“I wish they could just figure it out.”</span>
    </p>
    <p class="mt-4 text-sm lg:text-base/8 text-gray-700">
      This reaction reflects <span class="font-bold"
        >frustration without structure.</span
      >
    </p>
    <p class="mt-2 text-base/8 text-gray-700">
      You want people to take initiative - but they likely lack the guidance,
      context, or authority to make confident calls.
    </p>
    <p
      class="mt-2 text-sm lg:text-base/8 text-gray-700 px-0 lg:px-10 italic font-semibold"
    >
      If your team isn’t figuring it out, they’re not ready - and that’s a
      system failure, not a personal one.
    </p>

    <p class="mt-6 text-base lg:text-lg/8 text-gray-700 font-bold">
      Level-Up Opportunity
    </p>
    <p class="mt-2 text-sm lg:text-base/8 text-gray-700 ml-0 lg:ml-4">
      Turn frustration into structure. Ask: <span class="italic"
        >Have I defined what a good decision looks like in this context?
      </span>
    </p>
    <p
      class="mt-1 text-sm lg:text-base/8 text-gray-700 ml-0 lg:ml-4 font-semibold"
    >
      Clarity fuels confidence. Empowerment begins with expectations.
    </p>
  {:else if answer == 4}
    <p class="text-base lg:text-xl/8 text-gray-700">
      D: <span class="font-bold">“Everything bottlenecks at me.” </span>
    </p>
    <p class="mt-4 text-sm lg:text-base/8 text-gray-700">
      This is a red-alert signal that your <span class="font-bold"
        >organization’s decision-making system is broken.</span
      >
    </p>
    <p class="mt-2 text-sm lg:text-base/8 text-gray-700">
      You’ve become the central node - and it’s likely burning you out while
      stalling your team’s growth.
    </p>
    <p
      class="mt-2 text-sm lg:text-base/8 text-gray-700 px-0 lg:px-10 italic font-semibold"
    >
      When every decision stops at you, your team isn’t executing - they’re
      waiting.
    </p>

    <p class="mt-6 text-base lg:text-lg/8 text-gray-700 font-bold">
      Level-Up Opportunity
    </p>
    <p class="mt-2 text-sm lg:text-base/8 text-gray-700 ml-0 lg:ml-4">
      Pick one high-frequency decision and systematize it.
    </p>
    <p
      class="mt-1 text-sm lg:text-base/8 text-gray-700 ml-0 lg:ml-4 font-semibold"
    >
      Ask: <span class="italic"
        >What would it look like to hand it off - with clear criteria, steps,
        and check-ins?</span
      >
    </p>
    <p class="mt-2 text-sm lg:text-base/8 text-gray-700 ml-0 lg:ml-4">
      You don’t need to fix everything. You need to stop owning everything.
    </p>
  {/if}
</div>
